// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xProductPartNumbersStyles from "@adl/App/SharedStyle/ProductPartNumbers.styles";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ReorderPadProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ReorderPadProductAttributesStyles {
    container?: InjectableCss;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const reorderPadProductAttributesStyles: ReorderPadProductAttributesStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    attributeTypeText: {
        ...xProductPartNumbersStyles.label,
    },
    attributeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    wrapper: {
        css: css`
            display: flex;
        `,
    },
};

const ReorderPadProductAttributes: FC<Props> = ({ product: { properties }, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(reorderPadProductAttributesStyles, extendedStyles));
    const session = useAppSelector(state => getSession(state));

    if (!properties.lastOrderedQty && !properties.lastOrderedDate) {
        return null;
    }

    const displayAttribute = (label: string, value?: string) => {
        if (!value) {
            return null;
        }
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.attributeTypeText}>{label}</Typography>
                <Typography {...styles.attributeValueText}>{value}</Typography>
            </StyledWrapper>
        );
    };
    return (
        <StyledWrapper {...styles.container}>
            {displayAttribute(translate("Order QTY"), parseInt(properties.lastOrderedQty, 10).toString())}
            {displayAttribute(
                translate("Last Ordered"),
                properties.lastOrderedDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(properties.lastOrderedDate),
                          language: session.language,
                          options: {
                              year: "numeric",
                              month: "numeric",
                              day: "numeric",
                          },
                      })
                    : "",
            )}
        </StyledWrapper>
    );
};

export default withProduct(ReorderPadProductAttributes);
